import React, { useMemo, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import MyPage from '../../MyPage';
import SubTabsWarehouses from '../../SubTabsWarehouses';
import { isSuperAdmin } from '../../helpers/user';
import axiosService from '../../helpers/axios';

const API_UPLOAD = '/v1/purchasesupload/';

// The versioned (/v1/) API wraps payloads in {status, message, data}; unwrap it.
function unwrapV1(res) {
  const body = res?.data;
  if (body && typeof body === 'object' && 'data' in body && ('status' in body || 'message' in body)) {
    return body.data;
  }
  return body;
}

function formatError(err) {
  const data = err?.response?.data;
  if (!data) return err?.message || 'Request failed';
  if (typeof data === 'string') return data;
  const unwrapped = data?.data && typeof data.data === 'object' ? data.data : data;
  if (unwrapped.error) return unwrapped.error;
  if (unwrapped.message) return unwrapped.message;
  if (unwrapped.detail) return unwrapped.detail;
  if (data.message) return data.message;
  if (data.detail) return data.detail;
  const fieldErrors = Object.entries(unwrapped)
    .map(([key, val]) => `${key}: ${Array.isArray(val) ? val.join(', ') : val}`)
    .join('\n');
  return fieldErrors || 'Request failed';
}

export default function WarehousesPurchasesUpload() {
  const canEdit = isSuperAdmin();

  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null); // { message, created_count, updated_count, errors }

  const handleUpload = async () => {
    if (!file) {
      alert('Please select the file to upload.');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    setUploading(true);
    setResult(null);
    try {
      const res = await axiosService.post(API_UPLOAD, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const payload = unwrapV1(res) || {};
      setResult(payload);
      const errorCount = Array.isArray(payload.errors) ? payload.errors.length : 0;
      const summary =
        payload.message ||
        `Created ${payload.created_count ?? 0}, updated ${payload.updated_count ?? 0}.`;
      alert(errorCount ? `${summary}\n\n${errorCount} row(s) were skipped — see the details below.` : summary);
      setFile(null);
    } catch (err) {
      setResult(null);
      alert(formatError(err));
    } finally {
      setUploading(false);
    }
  };

  const errorColumnDefs = useMemo(
    () => [
      { field: 'row', headerName: 'CSV Row', width: 120, filter: 'agNumberColumnFilter' },
      { field: 'error', headerName: 'Reason', minWidth: 320, flex: 1 },
    ],
    []
  );

  const defaultColDef = useMemo(
    () => ({ sortable: true, resizable: true, filter: true, floatingFilter: true }),
    []
  );

  const errors = Array.isArray(result?.errors) ? result.errors : [];

  if (!canEdit) {
    return (
      <div className="scrollable-container">
        <MyPage />
        <SubTabsWarehouses />
        <div style={{ padding: '10px', fontSize: '13px' }}>
          You do not have permission to upload purchases.
        </div>
      </div>
    );
  }

  return (
    <div className="scrollable-container">
      <MyPage />
      <SubTabsWarehouses />

      <div style={{ padding: '10px' }}>
        <div style={{ fontWeight: 'bold', fontSize: '13px', marginBottom: '8px' }}>
          Purchases — Upload
        </div>
        <div style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
          Import a semicolon-separated SAP purchase export. Rows are matched on
          (Purchasing Document, Item): existing purchases are updated, new ones are created.
        </div>

        <div className="d-flex flex-wrap align-items-end gap-3 mb-3">
          <div style={{ minWidth: '240px' }}>
            <label className="form-label mb-1" style={{ fontSize: '12px' }}>
              File <span style={{ color: '#dc3545' }}>*</span>
            </label>
            <input
              type="file"
              className="form-control form-control-sm"
              accept=".csv,.txt,.xlsx,.xls"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              disabled={uploading}
            />
          </div>

          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={handleUpload}
            disabled={uploading || !file}
          >
            {uploading ? 'Uploading...' : 'Upload CSV'}
          </button>
        </div>

        {result && (
          <div className="mb-3">
            <div
              className="alert alert-info py-2 px-3"
              style={{ fontSize: '12px', marginBottom: '8px' }}
            >
              <strong>{result.message || 'Upload completed.'}</strong>
              <div>
                Created: {result.created_count ?? 0} &nbsp;|&nbsp; Updated: {result.updated_count ?? 0}{' '}
                &nbsp;|&nbsp; Skipped: {errors.length}
              </div>
            </div>

            <div
              className="alert alert-warning py-2 px-3"
              style={{ fontSize: '12px', marginBottom: '8px' }}
            >
              ⚠️ <strong>Reminder:</strong> Don&apos;t forget to update the records that were assigned{' '}
              <code>DEFAULT_WAREHOUSE</code> — replace it with the correct warehouse.
            </div>

            {errors.length > 0 && (
              <>
                <div style={{ fontSize: '12px', color: '#b45309', marginBottom: '6px' }}>
                  The following rows were skipped. Fix them in the source file and re-upload.
                </div>
                <div className="ag-theme-quartz" style={{ height: '40vh', width: '100%' }}>
                  <AgGridReact
                    rowData={errors}
                    columnDefs={errorColumnDefs}
                    defaultColDef={defaultColDef}
                    animateRows
                  />
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
